<!--  DEV NOTE: "<template v-slot:inProgressMessage>" deleted and added as a property on ProgressBar component following the progress-bar.stories -->
<template>
	<div class="payments-processing">
		<div class="payments-processing__content">
			<ProgressBar
				:stepCount="stepCount"
				:currentStep="currentStep"
				:complete="complete"
				headerTitle1="Payment(s) are currently being posted."
				headerTitle2="Please wait to print checks once payment(s) have finished posting."
				headerWarning="Do not leave or reload page"
				:inProgressTitle="inProgressTitle"
				:inProgressSubtitle="'Vendor: ' + vendorInfo"
			>
				<template v-slot:image>
					<img
						class="payments-processing__graphic"
						src="~@/assets/process-payments.gif"
						alt="Post Payments Animation"
					/>
				</template>
			</ProgressBar>
		</div>

		<Modal
			:visible="printChecksVisible"
			title="Print A/P Checks"
			@close="onPrintBatchNumbersCancel"
		>
			<template v-slot:body>
				<NotificationBlock
					v-if="postError"
					state="negative"
					iconClassName="icon-warning"
					title="Failed To Post"
				>
					<template v-slot:default>
						<div class="notification-block__message">
							<ul
								v-for="response in failedBatchNumber"
								:key="response.VendorNumber"
							>
								<li
									class="notification-block__text"
									ref="notificationMessage"
								>
									Vendor {{ response.VendorNumber }} -
									{{ response.ErrorMessage }}
								</li>
							</ul>
						</div>
					</template>
				</NotificationBlock>
				<div class="print-ap-checks-modal">
					<div
						v-if="postSuccess"
						class="print-ap-checks-modal__batch-text"
						ref="postSuccessMessage"
					>
						Successfully posted the following batches:
					</div>

					<ul
						class="print-ap-checks-modal__vendor-list"
						v-for="response in postedBatchNumber"
						:key="response.VendorNumber"
					>
						<li class="print-ap-checks-modal__vendor-item">
							<span
								class="print-ap-checks-modal__vendor-item-name"
							>
								Vendor: {{ response.VendorNumber }}
							</span>

							<div
								class="print-ap-checks-modal__vendor-item-posting"
								v-for="invoice in response.Invoices"
								:key="invoice.BatchNumber"
								v-html="invoice.HTMLString"
							></div>
						</li>
					</ul>
				</div>
			</template>

			<template v-slot:footer>
				<ButtonSet>
					<ButtonClassic
						variant="primary"
						labelText="Print Batch Numbers"
						@click="onPrintBatchNumbers"
						v-if="anyBatchToPrint"
					/>
					<ButtonClassic
						variant="text"
						labelText="Close"
						@click="onPrintBatchNumbersCancel"
						v-if="!anyCheckToPrint"
					/>
				</ButtonSet>
			</template>
		</Modal>

		<Modal
			title="Printing"
			variant="confirmation"
			:visible="printVisible"
			applyLabelText="Print Checks"
			@apply="onPrint"
			@cancel="onPrintCancel"
			@close="onPrintCancel"
		>
			<template v-slot:body>
				<div class="printing-modal">
					<div
						v-if="paymentMethodsEnabled"
						class="printing-modal__text"
					>
						The following check number(s) were applied:
						<br />
						<div
							v-for="(checks, index) in checksPrinted"
							:key="index"
						>
							<span>{{ checks }}</span>
						</div>
						<br />
						Click Print Checks to begin Printing
					</div>
					<p
						v-if="!paymentMethodsEnabled"
						class="printing-modal__text"
					>
						You have assigned check number(s): {{ checksPrinted }}
					</p>
				</div>
			</template>
		</Modal>

		<PageUnloadAlert />
	</div>
</template>

<script>
import pageMixin from '@/mixins/page-mixin';
import ProgressBar from '@/components/visual/progress-bar/progress-bar.vue';
import ButtonSet from '@/components/forms/button/button-set.vue';
import ButtonClassic from '@/components/forms/button/button-classic.vue';
import Modal from '@/components/shells/modal/modal.vue';
import ppPaymentProcessingImage from '@/assets/search.svg';
import { mapGetters, mapActions } from 'vuex';
// import buildRoute from '@/composables/build-route';
import postedChecksColumns from './posted-checks-columns';
import messageHandlerMixin from '@/mixins/message-handler-mixin';
import PageUnloadAlert from '@/components/notifications/page-unload-alert/page-unload-alert.vue';
import { AccountsPayable } from '@/constants/permissions';
import { Read } from '@/constants/permission-rights';
import Permission from '@/classes/permission';
import NotificationBlock from '@/components/notifications/notification-block/notification-block.vue';

export default {
	name: 'PaymentsProcessing',
	mixins: [pageMixin, messageHandlerMixin],
	metaInfo: {
		title: 'Payments Posting'
	},
	components: {
		ProgressBar,
		Modal,
		ButtonSet,
		ButtonClassic,
		PageUnloadAlert,
		NotificationBlock
	},
	data() {
		return {
			printChecksVisible: false,
			printVisible: false,
			postError: false,
			postSuccess: false,
			image: {
				src: ppPaymentProcessingImage,
				alt: 'Payment Posting'
			},
			postedBatchNumber: [],
			failedBatchNumber: [],
			rowKey: null,
			lockExpirationSeconds: 120,
			maintainLockTimeoutId: null
		};
	},
	props: {
		requiredPermissions: {
			type: Array,
			default: () => [new Permission(AccountsPayable, Read)]
		}
	},
	created() {
		this.hidePageLoader();
	},
	async mounted() {
		this.$nextTick(async () => {
			await this.runPaymentProcess();
		});
	},
	computed: {
		...mapGetters('communicationHub', ['apPostingProgress']),
		...mapGetters('accountsPayable', [
			'unpaidQueuedInvoicesVendorSummaries',
			'postedInvoiceResult',
			'printRowKey',
			'printBatchNumberUrl',
			'referenceNumbers',
			'paymentMethodsEnabled',
			'numberOfCopies'
		]),
		...mapGetters('principal', ['user']),
		// printPageUrl() {
		// 	//TODO: update this to get the value from the app store module
		// 	//when we've completed update to get the env configs from the AccessWeb api
		// 	//this is the route to AccessWebControllers.SystemController.CrossSitePrint
		// 	const page = process.env.VUE_APP_ACCESSWEB_PRINT_PAGE;
		// 	let url;

		// 	url = buildRoute(page, { query: { rowKey: this.rowKey } });

		// 	return url;
		// },
		complete() {
			let result = false;

			if (this.apPostingProgress) {
				result = this.apPostingProgress.complete;
			}

			return result;
		},
		vendorInfo() {
			let info = '';
			let s = '';

			if (
				this.apPostingProgress &&
				this.apPostingProgress.currentVendor
			) {
				if (this.apPostingProgress.currentVendor.invoiceCount > 1) {
					s = 's';
				}

				info = `${this.apPostingProgress.currentVendor.vendorNumber} -
						${this.apPostingProgress.currentVendor.vendorName}
						(${this.apPostingProgress.currentVendor.invoiceCount} total invoice${s})`;
			}

			return info;
		},
		stepCount() {
			let result = 0;

			if (this.apPostingProgress) {
				result = this.apPostingProgress.checkCount;
			}

			return result;
		},
		currentStep() {
			let result = 0;

			if (this.apPostingProgress) {
				result = this.apPostingProgress.currentCheckIndex;
			}

			return result;
		},
		checksPrinted() {
			if (this.paymentMethodsEnabled) {
				// Create a copy of the postedInvoiceResult array
				let response = [...this.postedInvoiceResult];

				// Extract check numbers using map and filter, excluding items with paymentMethodTypeId not equal to 1
				let checkNumbers = response
					.filter(
						item =>
							item.success &&
							item.data.CheckId != 0 &&
							item.data.paymentMethodTypesId === 1
					)
					.map(item => ({
						checkNumber: item.data.CheckNumber.replace(/\s+/g, ''),
						bankAccountPaymentMethodsId:
							item.data.bankAccountPaymentMethodsId
					}));

				// Group check numbers by bankAccountPaymentMethodsId
				let groupedCheckNumbers = checkNumbers.reduce((acc, item) => {
					if (!acc[item.bankAccountPaymentMethodsId]) {
						acc[item.bankAccountPaymentMethodsId] = [];
					}
					acc[item.bankAccountPaymentMethodsId].push(
						item.checkNumber
					);
					return acc;
				}, {});

				// Sort the check numbers within each group, treating numbers and letters separately
				for (let key in groupedCheckNumbers) {
					groupedCheckNumbers[key].sort((a, b) => {
						let numA = parseInt(a);
						let numB = parseInt(b);
						if (isNaN(numA) && isNaN(numB)) {
							return a.localeCompare(b);
						} else if (isNaN(numA)) {
							return 1;
						} else if (isNaN(numB)) {
							return -1;
						} else {
							return numA - numB;
						}
					});
				}

				// Initialize an empty array to store ranges of check numbers
				let ranges = [];

				// Loop through each group to create ranges
				for (let key in groupedCheckNumbers) {
					let checkNumbers = groupedCheckNumbers[key];
					let rangeStart = checkNumbers[0];
					let previousNumber = checkNumbers[0];
					let groupRanges = [];

					for (let i = 1; i < checkNumbers.length; i++) {
						let currentNumber = checkNumbers[i];
						let prevNum = parseInt(previousNumber);
						let currNum = parseInt(currentNumber);

						if (
							isNaN(prevNum) ||
							isNaN(currNum) ||
							currNum !== prevNum + 1
						) {
							if (rangeStart === previousNumber) {
								groupRanges.push(`${rangeStart}`);
							} else {
								groupRanges.push(
									`${rangeStart} - ${previousNumber}`
								);
							}
							rangeStart = currentNumber;
						}
						previousNumber = currentNumber;
					}

					// Add the last range to the groupRanges array
					if (rangeStart === previousNumber) {
						groupRanges.push(`${rangeStart}`);
					} else {
						groupRanges.push(`${rangeStart} - ${previousNumber}`);
					}

					// Join the group ranges with a comma and space, and add to the ranges array
					ranges.push(groupRanges.join(', '));
				}

				// Return the array of ranges
				return ranges;
			} else {
				let text = '';
				let response = [...this.postedInvoiceResult];
				while (response?.length > 0) {
					let lastResponse = response.shift();
					if (
						lastResponse.success &&
						lastResponse.data.CheckId != 0
					) {
						text +=
							text == ''
								? lastResponse.data.CheckNumber.trim()
								: ' - ' + lastResponse.data.CheckNumber.trim();
					}
				}
				return text;
			}
		},
		anyCheckToPrint() {
			let response = [...this.postedInvoiceResult];

			if (this.paymentMethodsEnabled) {
				if (
					response?.some(
						item =>
							item.success &&
							item.data.CheckId != 0 &&
							item.data.paymentMethodTypesId === 1
					)
				) {
					return true;
				}
				return false;
			} else {
				if (
					response?.some(
						item => item.success && item.data.CheckId != 0
					)
				) {
					return true;
				}
				return false;
			}
		},
		anyBatchToPrint() {
			let response = [...this.postedInvoiceResult];

			if (this.paymentMethodsEnabled) {
				if (
					response?.some(
						item =>
							item.success &&
							item.data.BatchNumber != 0 &&
							item.data.paymentMethodTypesId === 1
					)
				) {
					return true;
				}
				return false;
			} else {
				if (
					response?.some(
						item => item.success && item.data.BatchNumber != 0
					)
				) {
					return true;
				}
				return false;
			}
		},
		sandboxVals() {
			const sandbox = [
				'allow-forms',
				'allow-modals',
				'allow-popups',
				'allow-scripts',
				'allow-top-navigation',
				'allow-same-origin',
				'allow-top-navigation'
			];
			let vals = '';

			if (sandbox) {
				vals = sandbox.join(' ');
			}

			return vals;
		},
		inProgressTitle() {
			let title;

			if (this.stepCount) {
				title = `Posting A/P Check Entries (${this.currentStep} of ${this.stepCount})`;
			}

			return title;
		},
		vendorPaymentSummaries() {
			let selectedItems = this.unpaidQueuedInvoicesVendorSummaries.filter(
				x => x.selected
			);
			return selectedItems;
		},
		vendorPaymentSummaryIds() {
			let ids = [];

			if (this.vendorPaymentSummaries) {
				ids = this.vendorPaymentSummaries.map(s => s.vendorId);
			}

			return ids;
		}
	},
	methods: {
		...mapActions('communicationHub', ['setAPPostingProgress']),
		...mapActions('accountsPayable', [
			'postQueuedInvoice',
			'updatePaymentSelection',
			'getPrintRowKey',
			'getPrintBatchNumberUrl',
			'releaseVendorsForProcessingAction',
			'lockVendorsForProcessingAction',
			'clearReferenceNumbersAction',
			'cleanNumberOfCopiesAction'
		]),
		...mapActions('printing', ['setPrintQueueMultipleRowKeyAction']),
		releaseVendors() {
			this.releaseVendorsForProcessingAction(this.user);
			clearTimeout(this.maintainLockTimeoutId);
		},
		async lockVendors() {
			await this.lockVendorsForProcessingAction({
				vendorIds: this.vendorPaymentSummaryIds,
				user: this.user,
				expirationSeconds: this.lockExpirationSeconds
			});
			this.maintainLock();
		},
		maintainLock() {
			this.maintainLockTimeoutId = setTimeout(
				this.lockVendors,
				this.lockExpirationSeconds * 1000
			);
		},
		async runPaymentProcess() {
			//Load selected vendors summary from state
			const postingList = this.unpaidQueuedInvoicesVendorSummaries.filter(
				x => x.selected
			);

			//Load post option
			const query = { ...this.$route.query };
			let _postAhead = false;
			if (query.postoption) {
				_postAhead = query.postoption == 'ahead' ? true : false;
			}

			if (postingList.length > 0) {
				const checkCount = postingList.length;
				let _currentCheckIndex = 1;
				let _flushState = true;

				this.maintainLock();

				while (postingList.length > 0) {
					const vendorItem = postingList.shift();
					if (vendorItem.childRecordset.length > 0) {
						let _invoices = [];
						vendorItem.childRecordset.forEach(item =>
							_invoices.push(item.id)
						);

						//Update Progressbar status
						await this.setAPPostingProgress({
							checkCount: checkCount,
							currentCheckIndex: _currentCheckIndex,
							complete: false,
							currentVendor: {
								id: vendorItem.vendorId,
								vendorName: vendorItem.vendorName,
								invoiceCount: vendorItem.childRecordset.length,
								vendorNumber: vendorItem.vendorNumber
							}
						});

						let referenceNumber = null;

						if (this.referenceNumbers[vendorItem.vendorId] != '') {
							referenceNumber = this.referenceNumbers[
								vendorItem.vendorId
							];
						}

						const params = {
							parameters: {
								dealerGuid: this.user.AspNetCompaniesId,
								dealerNumber: 'dealerNumber', //isn't used -- can be anything
								companyId: this.user.CurrentEmployeeCompanyId
							},
							model: {
								InvoiceIds: _invoices,
								BankAccountId: vendorItem.bankAccount,
								For: vendorItem.for,
								Description: vendorItem.description,
								PostAhead: _postAhead,
								VendorNumber: vendorItem.vendorNumber,
								ReferenceNumber: referenceNumber,
								bankAccountPaymentMethodsId:
									vendorItem.bankAccountPaymentMethodsId
							},
							flushState: _flushState
						};
						await this.postQueuedInvoice(params);
						await this.$nextTick();

						//Once the vendor is processed, is removed from the payment selection
						//If the user makes a page refresh, this vendor won't be processed
						await this.updatePaymentSelection({
							value: false,
							vendorId: vendorItem.vendorId,
							propertyName: 'selected'
						});
					}
					_flushState = false;
					_currentCheckIndex += 1;
				}

				//Push complete status in the progress bar
				await this.setAPPostingProgress({
					checkCount: checkCount,
					currentCheckIndex: checkCount + 1,
					complete: true,
					currentVendor: {}
				});

				//Built posted result message
				let response = [...this.postedInvoiceResult];
				let vendorNumber;
				let vendorName;

				this.postedBatchNumber = [];
				this.failedBatchNumber = [];
				while (response?.length > 0) {
					let lastResponse = response.shift();
					if (lastResponse.success) {
						vendorNumber = lastResponse.data.VendorNumber ?? '';
						vendorName = lastResponse.data.VendorName ?? '';
						var itemIndex = this.postedBatchNumber.findIndex(
							x => x.VendorNumber == vendorNumber
						);
						if (itemIndex == -1) {
							this.postedBatchNumber.push({
								VendorNumber: vendorNumber,
								VendorName: vendorName,
								Invoices: []
							});
							itemIndex = this.postedBatchNumber.length - 1;
						}
						this.postedBatchNumber[itemIndex].Invoices.push({
							Success: lastResponse.success,
							BatchNumber: lastResponse.data.BatchNumber ?? '',
							CheckNumber: lastResponse.data.CheckNumber ?? '',
							HTMLString: lastResponse.data.HTMLString ?? ''
						});
						this.postSuccess = true;
					} else {
						this.failedBatchNumber.push({
							VendorNumber: lastResponse.data.VendorNumber,
							ErrorMessage: lastResponse.data.HTMLString
						});
						this.postError = true;
					}
				}

				this.printChecksVisible = true;

				this.clearReferenceNumbersAction();
			}
		},
		async onPrintBatchNumbers() {
			let apiResponse = [...this.postedInvoiceResult];
			let batchList = [];

			while (apiResponse.length > 0) {
				const response = apiResponse.shift();
				if (response.data) {
					batchList.push({
						VendorId: response.data.VendorId,
						VendorNumber: response.data.VendorNumber,
						VendorName: response.data.VendorName,
						CompanyId: response.data.CompanyId,
						BatchNumber: response.data.BatchNumber,
						Amount: response.data.Amount,
						Description: response.data.Description
					});
				}
			}
			const params = {
				parameters: {
					dealerGuid: this.user.AspNetCompaniesId,
					dealerNumber: 'dealerNumber', //isn't used -- can be anything
					companyId: this.user.CurrentEmployeeCompanyId
				},
				model: {
					FileName: 'ExcelAPPrintedChecks',
					BatchList: batchList,
					GridColumns: postedChecksColumns
				}
			};
			await this.getPrintBatchNumberUrl(params);
			let url = this.printBatchNumberUrl;

			window.open(url, '_blank');

			this.printChecksVisible = false;
			this.printVisible = true;
		},
		onPrintBatchNumbersCancel() {
			this.releaseVendors();
			this.printChecksVisible = false;
			if (this.anyCheckToPrint) {
				this.printVisible = true;
			} else {
				this.$router.push({
					name: 'process-payments',
					query: { tab: 2 }
				});
			}
		},
		async onPrint() {
			let apiResponse = [...this.postedInvoiceResult];
			let multiplePrintingModel = [];

			this.showPageLoader();

			while (apiResponse.length > 0) {
				const response = apiResponse.shift();
				if (response.data.MultiplePrintingModels?.length > 0) {
					const vendor = this.vendorPaymentSummaries.find(
						v => v.vendorId === response.data.VendorId
					);

					response.data.MultiplePrintingModels.forEach(item => {
						const isCopy = item.filename.includes('copy');

						const bankAccountId = this.paymentMethodsEnabled
							? vendor.paymentMethod.bankAccountsId
							: vendor.bankAccount;

						const copies =
							isCopy && vendor
								? this.numberOfCopies[bankAccountId]
								: 1;

						multiplePrintingModel.push(...Array(copies).fill(item));
					});
				}
			}

			if (multiplePrintingModel.length > 0) {
				//Load rowkey value
				const params = {
					parameters: {
						dealerGuid: this.user.AspNetCompaniesId,
						dealerNumber: 'dealerNumber', //isn't used -- can be anything
						companyId: this.user.CurrentEmployeeCompanyId
					},
					multiplePrintingModel: multiplePrintingModel
				};

				await this.getPrintRowKey(params);

				//setting the rowKey will trigger an iframe load
				//and iframed page printQueueMultiple.cshtml posts a message event
				//that gets handled by onMessage
				this.setPrintQueueMultipleRowKeyAction(this.printRowKey);
			}

			this.releaseVendors();

			this.printVisible = false;

			this.hidePageLoader();

			this.cleanNumberOfCopiesAction();

			this.$router.push({
				name: 'process-payments',
				query: { tab: 2 }
			});
		},
		onPrintCancel() {
			this.releaseVendors();
			this.printVisible = false;
			this.$router.push({
				name: 'process-payments',
				query: { tab: 2 }
			});
		}
		// async onIframeLoad() {
		// 	await this.$nextTick();
		// 	// console.log('onIframeLoad');

		// 	const iframe = this.$refs.printPageIframe;
		// 	if (iframe) {
		// 		//this calls the onPrint method in Views\System\Printing\PrintQueueMultiple.cshtml
		// 		iframe.contentWindow.onPrint();
		// 	}

		// 	this.hidePageLoader();

		// 	// this.$router.push({
		// 	// 	name: 'process-payments',
		// 	// 	query: { tab: 2 }
		// 	// });
		// },
		// onIframeError() {
		// 	// console.log('onIframeError');
		// },
		// onMessage(message) {
		// 	//gets called from message-handler-mixin
		// 	//iframe load event was not firing so getting a message event from the iframe
		// 	if (message && message.printQueueMultiple) {
		// 		this.onIframeLoad();
		// 	}
		// }
	}
};
</script>

<style lang="scss" scoped>
.payments-processing {
	&__content {
		@include transform-full-center;
		top: 35%;
		text-align: center;
	}
}

.print-ap-checks-modal {
	margin-top: $gutter-lg;
	min-width: 320px;

	&__batch-text {
		font-size: $txt-font-size;
	}

	&__vendor-list {
		display: flex;
		flex-direction: column;
		margin-top: $gutter-lg;
	}

	&__vendor-item {
		&:not(:first-child) {
			margin-top: $gutter-md;
		}

		&-name {
			@include heading('sm');
		}

		&-posting {
			margin-top: $gutter-sm;
			font-size: $txt-font-size;
		}
	}
}

.notification-block {
	&__message {
		display: flex;
		flex-direction: column;
		gap: $gutter-lg;
	}
}

.printing-modal {
	min-width: 340px;

	&__text {
		@include heading('sm');
	}
}
</style>
